import { useI18n } from "../lib/i18n.jsx";
import { formatPrice } from "../lib/format.js";
import { trackLead } from "../lib/analytics.js";
import { MapPin, Clock, ArrowRight, Plane } from "lucide-react";

const DAYS_PL_SHORT = ["ndz", "pn", "wt", "śr", "czw", "pt", "sob"];
const DAYS_EN_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseIsoDate(iso) {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d);
}

const DAYS_UK_SHORT = ["нд", "пн", "вт", "ср", "чт", "пт", "сб"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function dateParts(iso, lang) {
  const d = parseIsoDate(iso);
  const days = lang === "uk" ? DAYS_UK_SHORT : lang === "en" ? DAYS_EN_SHORT : DAYS_PL_SHORT;
  return {
    day: pad(d.getDate()),
    month: pad(d.getMonth() + 1),
    year: d.getFullYear(),
    weekday: days[d.getDay()],
  };
}

function diffDays(startIso, endIso) {
  const s = parseIsoDate(startIso).getTime();
  const e = parseIsoDate(endIso).getTime();
  return Math.max(1, Math.round((e - s) / 86400000));
}

export default function TripCard({ trip }) {
  const { t, lang } = useI18n();
  const isSummer = trip.season === "summer";
  const headerBg = isSummer
    ? "bg-gradient-to-br from-summer to-summer/80"
    : "bg-gradient-to-br from-winter to-winter/80";
  const btnClass = isSummer ? "btn-pill-primary" : "btn-pill-winter";

  const disabled = !trip.available;
  const disabledLabel = trip.is_past
    ? { pl: "Po terminie", uk: "Завершено", en: "Past" }[lang] || "Past"
    : trip.is_disabled
      ? { pl: "Niedostępny", uk: "Недоступно", en: "Unavailable" }[lang] || "Unavailable"
      : null;

  const turnLabel = { pl: "Turnus", uk: "Зміна", en: "Turn" }[lang] || "Turn";
  const seasonLabel = isSummer
    ? { pl: "Lato", uk: "Літо", en: "Summer" }[lang] || "Summer"
    : { pl: "Zima", uk: "Зима", en: "Winter" }[lang] || "Winter";

  const showMin30 = trip.min_price_30d != null && trip.is_promo;
  const currentPrice = trip.promo_price ?? trip.price;

  const cta = trip.is_sold_out
    ? { pl: "Lista rezerwowa", uk: "Список очікування", en: "Waiting list" }[lang] || "Waiting list"
    : trip.is_past
      ? { pl: "Po terminie", uk: "Завершено", en: "Past" }[lang] || "Past"
      : t.pricing.cta;
  const comingSoonLabel = { pl: "Zapisy wkrótce", uk: "Реєстрація скоро", en: "Signups soon" }[lang] || "Signups soon";

  // Diagonal ribbon (top-left corner) with state-priority. Replaces the
  // small pill badge — much more visible in a grid of cards.
  // is_past has ABSOLUTE priority — past trips show no ribbon, no promo
  // emphasis, and the whole card is grayscaled & non-interactive. Skip all
  // ribbon/emphasis logic when the trip is in the past.
  let ribbon = null;
  let savingsAmount = null;
  if (!trip.is_past) {
    if (trip.is_coming_soon) {
      ribbon = {
        text: { pl: "WKRÓTCE", uk: "СКОРО", en: "SOON" }[lang] || "SOON",
        cls: "bg-summer text-cream",
        pulse: false,
      };
    } else if (trip.is_promo) {
      if (trip.promo_price && trip.price) {
        savingsAmount = trip.price - trip.promo_price;
      }
      ribbon = {
        text: { pl: "PROMOCJA", uk: "АКЦІЯ", en: "PROMO" }[lang] || "PROMO",
        cls: "bg-sun text-winter",
        pulse: false,
      };
    } else if (trip.is_few_left) {
      ribbon = {
        text: { pl: "OSTATNIE", uk: "ОСТАННІ", en: "FEW LEFT" }[lang] || "FEW LEFT",
        cls: "bg-sky-600 text-white",
        pulse: true,
      };
    } else if (trip.is_sold_out) {
      ribbon = {
        text: { pl: "BRAK MIEJSC", uk: "ПРОДАНО", en: "SOLD OUT" }[lang] || "SOLD OUT",
        cls: "bg-red-600 text-cream",
        pulse: false,
      };
    }
  }

  const start = dateParts(trip.date_start, lang);
  const end = dateParts(trip.date_finish, lang);
  const days = diffDays(trip.date_start, trip.date_finish);

  // Promo cards get extra visual emphasis — sun-coloured ring & glow.
  // Past trips never get the emphasis, even if they were marked promo.
  const promoEmphasis = trip.is_promo && !trip.is_past
    ? "ring-2 ring-sun shadow-[0_15px_40px_-10px_rgba(244,196,48,0.35)]"
    : "";

  // Past trips: always grayscale + disabled, regardless of sold-out / few-left
  // / promo state. Other disabled states keep the existing "lista rezerwowa"
  // affordance for sold-out cards. Coming-soon cards stay FULL COLOUR — they
  // are an optimistic preview, not a dead trip.
  const dimmed =
    trip.is_past ||
    (disabled && !trip.is_sold_out && !trip.is_coming_soon);

  return (
    <div
      data-testid={`trip-card-${trip.shortcode}`}
      data-disabled={disabled ? "true" : "false"}
      className={`group relative h-full ${dimmed ? "opacity-55 grayscale pointer-events-none" : ""}`}
    >
      {/* Boarding-pass shell.
          The "perforation" zones are bg-cream notches positioned on the
          card's left/right edges to simulate ticket punch holes. */}
      <div
        className={`relative h-full flex flex-col bg-white rounded-3xl border-2 overflow-hidden shadow-[0_10px_30px_-12px_rgba(27,41,73,0.18)] ring-1 ring-winter/10 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_20px_45px_-12px_rgba(27,41,73,0.28)] ${promoEmphasis}`}
      >
        {/* Diagonal ribbon (top-left). Sits above the header with pointer-events
            disabled so it never blocks the CTA. */}
        {ribbon && (
          <div
            data-testid={`trip-ribbon-${trip.shortcode}`}
            aria-hidden="true"
            className="absolute top-0 left-0 w-32 h-32 overflow-hidden pointer-events-none z-20"
          >
            <span
              className={`absolute top-[30px] -left-[40px] w-[170px] text-center font-display font-extrabold text-[12px] tracking-[0.2em] py-2 -rotate-45 shadow-lg ring-1 ring-black/10 ${ribbon.cls} ${ribbon.pulse ? "animate-pulse-soft" : ""}`}
            >
              {ribbon.text}
            </span>
          </div>
        )}

        {/* MAIN body: from → to dates + duration */}
        <div className="relative px-6 pt-6 pb-5 flex-1">
          <div className="mx-auto w-20 h-20 mb-5 flex items-center justify-center border-4 border-summer font-display font-bold text-summer rounded-full text-md">{trip.shortcode}</div>
          <div className="items-center justify-center mb-5 text-lg text-winter leading-none tabular-nums text-center">
            {turnLabel} {trip.turn_number} · {seasonLabel}
          </div>
          <div className="items-center justify-center text-xl font-display font-bold text-winter uppercase leading-none tabular-nums text-center">
            {start.day}.{start.month} ({start.weekday}) - {end.day}.{end.month} ({end.weekday})
          </div>

          {/* Destination */}
          <div className="mt-4 flex items-center justify-center gap-1.5 text-sm text-winter/70">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="font-semibold">{trip.destination}</span>
          </div>
        </div>

        {/* PERFORATION line with side notches */}
        <div className="relative">
          {/* Left notch */}
          <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-cream" />
          {/* Right notch */}
          <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-cream" />
          <div className="border-t-2 border-dashed border-winter/15 mx-5" />
        </div>

        {/* STUB: price + CTA */}
        <div className="px-6 pt-5 pb-6 bg-paper/60">
          <div className="flex items-end justify-between gap-3 mb-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-winter/45 mb-1">
                {{ pl: "Cena obozu", uk: "Ціна табору", en: "Camp fare" }[lang] || "Camp fare"}
              </p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className={`font-display font-bold leading-none tabular-nums ${
                    trip.is_sold_out ? "text-winter/45" : "text-winter"
                  }`}
                  style={{ fontSize: "clamp(1.5rem, 3.2vw, 1.5rem)" }}
                >
                  {formatPrice(currentPrice)}
                </span>
                {trip.is_promo && savingsAmount > 0 && (
                  <span
                    data-testid={`trip-savings-${trip.shortcode}`}
                    className="font-display font-extrabold text-winter bg-sun text-[11px] tracking-wider uppercase px-2.5 py-1 rounded-full leading-none shadow-sm"
                  >
                    {(() => {
                      const labels = {
                        pl: `−${formatPrice(savingsAmount)}`,
                        uk: `−${formatPrice(savingsAmount)}`,
                        en: `Save ${formatPrice(savingsAmount)}`,
                      };
                      return labels[lang] || labels.en;
                    })()}
                  </span>
                )}
              </div>
              {trip.is_promo && (
                <p className="text-[11px] text-winter/55 mt-1.5">
                  {{ pl: "Cena standardowa: ", uk: "Стандартна ціна: ", en: "Regular price: " }[lang] || "Regular price: "}
                  <span className="line-through">{formatPrice(trip.price)}</span>
                </p>
              )}
              {showMin30 && (
                <p className="text-[10px] text-winter/55 mt-0.5" data-testid="trip-omnibus">
                  {(() => {
                    const labels = {
                      pl: `Najniższa z 30 dni: ${formatPrice(trip.min_price_30d)}`,
                      uk: `Найнижча за 30 днів: ${formatPrice(trip.min_price_30d)}`,
                      en: `Lowest in 30d: ${formatPrice(trip.min_price_30d)}`,
                    };
                    return labels[lang] || labels.en;
                  })()}
                </p>
              )}
            </div>

            {!trip.is_sold_out && trip.is_few_left && (
              <p className="shrink-0 flex items-center gap-1.5 text-[10px] text-sun-foreground bg-sun/25 rounded-full px-3 py-1.5 font-bold leading-none whitespace-nowrap">
                <Clock className="w-3.5 h-3.5" />
                {(() => {
                  const labels = {
                    pl: `Zostało ${trip.available_reservations}`,
                    uk: `Залишилось ${trip.available_reservations}`,
                    en: `${trip.available_reservations} left`,
                  };
                  return labels[lang] || labels.en;
                })()}
              </p>
            )}
          </div>

          {trip.is_coming_soon ? (
            <span className="btn-pill w-full justify-center bg-summer/15 text-summer cursor-not-allowed" style={{ boxShadow: "none" }}>
              {comingSoonLabel}
            </span>
          ) : trip.is_past || (disabled && !trip.is_sold_out) ? (
            <span className="btn-pill w-full justify-center bg-winter/10 text-winter/50 cursor-not-allowed" style={{ boxShadow: "none" }}>
              {disabledLabel}
            </span>
          ) : (
            <a
              href={trip.booking_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`trip-cta-${trip.shortcode}`}
              onClick={() => {
                if (trip.is_sold_out) return;
                trackLead({
                  value: trip.promo_price ?? trip.price,
                  contentName: `${trip.shortcode} ${trip.destination || ""}`.trim(),
                  contentId: trip.shortcode,
                });
              }}
              className={`btn-pill ${trip.is_sold_out ? "btn-pill-outline" : btnClass} w-full justify-center`}
            >
              {cta} →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
